import React, { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import CropAndRotateImage from "../CropAndRotateImage/CropAndRotateImage";
import DragAndDropCard from "../DragAndDropCard/DragAndDropCard";
import useUploadedFileValidations from "../../CustomHooks/useUploadedFileValidations";
import { getImageSource } from "../../utils/getImageSource";
import PhotoGallery from "../PhotoGallery/PhotoGallery";
import styles from "./UploadImageStyles";

const UploadImage = ({
  errorWhileUpload = "",
  fileUploadResult = "",
  handleFileUpload = () => {},
  hideIconDelete,
  isDocumentUpload = false,
  isUploadingImageToServer = false,
  onDeleteImage = () => {},
  openCropViewAfterImageSelection = false,
  setFileUploadResult = () => {},
  uploadPercentage = 0,
  customContentContainerStyle = {},
  shouldOpenInModal,
  customImageStyles,
  isVideoUpload
}) => {
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [openCropView, setOpenCropView] = useState(false);

  const {
    nonUploadableImageError,
    fileTooLargeError,
    initiateFileUpload,
    invalidFormatError,
  } = useUploadedFileValidations({ isDocumentUpload, isVideoUpload });

  const imageUploadedToServer = fileUploadResult;

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };
  const onValidImageUpload = ({ uploadedFile }) => {
    setFile(uploadedFile);
    openCropViewAfterImageSelection &&  !isVideoUpload && setOpenCropView(true);
    if (!openCropViewAfterImageSelection || isVideoUpload) {
      handleFileUpload({
        file: uploadedFile,
        errorCallback: () => {
          setFile(null);
        },
      });
    }
  };

  const fileUploadHandler = (e) => {
    initiateFileUpload({
      onLoad: onValidImageUpload,
      resetUploadInput: () => (e.target.value = null),
      uploadedFile: e.target.files[0],
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    initiateFileUpload({
      onLoad: onValidImageUpload,
      resetUploadInput: () => (e.target.value = null),
      uploadedFile: e.dataTransfer.files?.[0],
    });
  };

  const handleDeleteImage = (idx) => {
    setFile(null);
    setFileUploadResult("");
    onDeleteImage(idx);
  };

  const fileName = useMemo(() => {
    return getImageSource(file);
  }, [file]);

  return (
    <>
      {!!file && openCropView && (
        <CropAndRotateImage
          {...{
            file,
            handleFileUpload,
            initiateFileUpload,
            photoURL: fileName,
            setFile,
            setOpenCropView,
            shouldOpenInModal,
            errorWhileUpload,
            isLoading: isUploadingImageToServer,
            onClose: () => {
              setOpenCropView(false);
            },
          }}
        />
      )}
      {!isUploadingImageToServer &&
        !!imageUploadedToServer &&
        !openCropView && (
          <PhotoGallery
            photos={[imageUploadedToServer]}
            customImageStyles={{...styles.imageStyle,...customImageStyles}}
            onRemoveImage={(idx) => handleDeleteImage(idx)}
            hideIconDelete={hideIconDelete}
          />
        )}
      {!imageUploadedToServer && (
        <DragAndDropCard
          {...{
            errorMessage:
              nonUploadableImageError ||
              fileTooLargeError ||
              invalidFormatError ||
              errorWhileUpload,
            fileInputRef,
            fileUploadHandler,
            handleDragOver,
            handleDrop,
            handleUploadClick,
            isDocumentUpload,
            isVideoUpload,
            isLoading: isUploadingImageToServer,
            uploadPercentage,
            customContentContainerStyle: customContentContainerStyle,
          }}
        />
      )}
    </>
  );
};

UploadImage.propTypes = {
  errorWhileUpload: PropTypes.string,
  fileUploadResult: PropTypes.string,
  handleFileUpload: PropTypes.func,
  hideIconDelete: PropTypes.bool,
  isDocumentUpload: PropTypes.bool,
  isUploadingImageToServer: PropTypes.bool,
  onDeleteImage: PropTypes.func,
  openCropViewAfterImageSelection: PropTypes.bool,
  setFileUploadResult: PropTypes.func,
  uploadPercentage: PropTypes.number,
  customContentContainerStyle: PropTypes.object,
  isAllFormat: PropTypes.bool,
  isImageAndPDF: PropTypes.bool,
};

export default UploadImage;
